import React, { memo, useEffect, useRef, useMemo, useState, useCallback } from 'react';
import Drawer from '@material-ui/core/Drawer';
import { CircularProgress, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useStoreState } from 'easy-peasy';
import { ChevronRightRounded } from '@material-ui/icons';
import pick from 'lodash/pick';
import Editor from '../components/Editor';
import {
	doInComponentSelected,
	doInComponentUpdate,
	onInitEditor,
	onLoadEditor,
	loadTemplateIntoEditor,
	getWindowSize,
	generateUrlFromLocalStorage,
	checkWindowVariableChangedToTrue,
	getTemplateType,
	getBase64FileSize,
	removeComponentFromModel,
	clearMaxFileSizeErrorInAssetManager,
	displayMaxFileSizeErrorInAssetManager,
	handleComponentDeselected,
	addDrawerOpenCloseClasses,
	setStyleGrapesJSComponent,
	convertPxToRelative,
	removeTagsFromString,
} from '../utils';

import EditorLeftPanel from '../components/EditorLeftPanel';
import { BRANDING_TYPE } from '../constants';
import {
	createWidgetItemCommand,
	createAccoladeItemCommand,
	createVideoItemCommand,
	createChangeBackgroundCommand,
	createServiceItemCommand,
	createLayoutEditorCommand,
	createOpenEditorDialogCommand,
} from '../utils/commandUtils';
import ChangeBackgroundDialog from '../components/dialog/ChangeBackgroundDialog';
import EditConfigurationDialog from '../components/dialog/EditConfigurationDialog';
import getEditorConfig from '../configs/editor';

const getFrameStyles = (template_type) => {
	let frameStyles = {};
	if (
		template_type === BRANDING_TYPE.TYPE_REPORT_COVER ||
		template_type === BRANDING_TYPE.TYPE_EBOOK_INTRO
	) {
		const ratio = 595 / 842;
		const size = getWindowSize();
		let frameHeight = size.height;
		let frameWidth = frameHeight * ratio;
		if (frameWidth > size.width) {
			frameWidth = size.width;
			frameHeight = frameWidth / ratio;
		}
		frameStyles = {
			width: frameWidth,
			height: frameHeight,
		};
	}
	// console.log('EditorScreen getFrameStyles', frameStyles);
	return frameStyles;
};

const useStyles = makeStyles((theme) => {
	// console.log('EditorScreen useStyles', props);
	return {
		'@global': {
			'.gjs-frame': (props) => ({
				...props.frameStyles,
			}),
		},
		openDrawerWrapperStyle: {
			position: 'absolute',
			top: 10,
			left: 0,
			width: 40,
			height: 40,
			zIndex: 10,
		},
		openDrawerIconStyle: {
			background: theme.palette.text.primary,
			color: theme.palette.background.paper,
			borderBottomLeftRadius: 0,
			borderTopLeftRadius: 0,
			borderBottomRightRadius: theme.spacing(1),
			borderTopRightRadius: theme.spacing(1),
			'&:hover': {
				background: theme.palette.text.primary,
				opacity: 0.8,
			},
		},
		drawerStyle: {},
		drawerPaper: {
			transition: 'width 0.25s',
			'&.opened': {
				width: '25vw',
				minWidth: '255px',
			},
			'&.closed': {
				width: 0,
			},
		},
		progressContainer: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			zIndex: 100000,
			paddingLeft: '25%',
		},
	};
});

const EditorScreen = () => {
	const editorRef = useRef();
	const [sectionId, setSectionId] = useState('0');
	const [drawerOpen, setDrawerOpen] = useState(true);
	const [videoContainerId, setVideoContainerId] = useState();
	const [widgetContainerId, setWidgetContainerId] = useState();
	const [changeBackgroundDialogOptions, setChangeBackgroundDialogOptions] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
	const [configurationEditorOpen, setConfigureDialogOpen] = useState(false);
	const templateData = useStoreState((state) => state.template);
	const [templateLoaded, setTemplateLoaded] = useState(false);

	const templateId = templateData.template;

	if (!window.fromHome) {
		const url = generateUrlFromLocalStorage();
		window.open(url, '_self');
	}

	const openChangeBackgroundDialogOpen = (open, { sectionId, ...options }) => {
		console.log('openChangeBackgroundDialogOpen', sectionId);
		setSectionId(sectionId);
		setChangeBackgroundDialogOptions(open && options);
	};

	const openConfigurationDialog = (open, { sectionId, videoContainerId, widgetContainerId }) => {
		// console.log('openConfigurationDialog', sectionId);
		setSectionId(sectionId);
		setVideoContainerId(videoContainerId);
		setWidgetContainerId(widgetContainerId);
		setConfigureDialogOpen(open);
	};

	const openLayoutDialog = (open, { sectionId }) => {
		setSectionId(sectionId);
		setLayoutEditorOpen(open);
	};

	const closeChangeBackgroundDialog = () => {
		setChangeBackgroundDialogOptions(null);
	};

	const toggleConfigureDialog = () => {
		setConfigureDialogOpen(!configurationEditorOpen);
	};

	const templateType = getTemplateType(templateId);
	const template = {
		id: templateId,
		type: templateType,
	};
	// console.log('EditorScreen templateType', templateType);
	const frameStyles = getFrameStyles(templateType);
	const classes = useStyles({ frameStyles });

	useEffect(() => {
		if (templateLoaded && window.HelpHero?.startTour) {
			if (templateType === BRANDING_TYPE.TYPE_REPORT_COVER) {
				window.HelpHero.startTour('8blDceXliK6', {
					skipIfAlreadySeen: true,
				});
			} else if (templateType === BRANDING_TYPE.TYPE_WEBSITE) {
				const tourId = window.locale === 'th-TH' ? 'k0272GC9t4' : 'KwwIepZlJss';
				window.HelpHero.startTour(tourId, {
					skipIfAlreadySeen: true,
				});
			}
		}
	}, [templateId, templateLoaded]);

	useEffect(() => {
		checkWindowVariableChangedToTrue('grapesjs').then(() => {
			setTimeout(async () => {
				await loadTemplateIntoEditor(template);
				setTemplateLoaded(true);
			}, 1000);
		});
	}, []);

	const toggleDrawer = () => {
		console.log(drawerOpen);
		setDrawerOpen(!drawerOpen);

		addDrawerOpenCloseClasses(drawerOpen);

		setTimeout(() => {
			window.grapesjs.refresh();
		}, 500);
	};

	const onInit = useCallback(
		(editor) => {
			window.CKEDITOR.on('instanceReady', function (ev) {
				ev.editor.on('paste', function (evt) {
					console.log('EditorScreen onInit paste', {
						dataValue: evt.data.dataValue,
						evt,
					});
					evt.data.dataValue = removeTagsFromString(evt.data.dataValue, ['span']);
					// evt.data.dataValue = evt.data.dataValue.replace(/<[^>]*?>/g, '');
				});
			});

			if (editor && !window.grapesjs) {
				window.grapesjs = editor;
			}

			// Disable double click open asset manager
			editor.Commands.add('open-assets', () => {
				// console.log('do nothing');
			});

			onInitEditor(editor, templateId);
			editor.on('load', () => {
				onLoadEditor(editor, 'load');
			});

			editor.on('component:update', (component) => {
				doInComponentUpdate(editor, component);
			});

			editor.on('change:device', () => {
				const device = editor.getDevice();
				console.log('Current device: ', device);
				if (device !== 'Desktop') {
					window.grapesjs.Canvas.getWindow().unapplyOkayNav();
				} else {
					setTimeout(() => {
						window.grapesjs.Canvas.getWindow().unapplyAndApplyOkayNav?.();
					}, 500);
				}
			});

			editor.on('change:changesCount', () => {
				window.onbeforeunload = null;
			});

			editor.SelectorManager.getAll().each((selector) => selector.set('private', 1));
			editor.on('selector:add', (selector) => selector.set('private', 1));

			//	Assets managers events
			editor.on('asset:add', (model) => {
				// console.log('asset:add', model);
				const imageSrc = model.id;
				// console.log('asset:add', imageSrc);
				if (imageSrc.startsWith('data:')) {
					// clear existing error
					clearMaxFileSizeErrorInAssetManager();

					//  data:image/png;base64,iVBORw0KG
					const fileSize = getBase64FileSize(imageSrc);
					// console.log('asset:add', imageSrc, fileSize);
					// if > 2mb, not allow
					if (fileSize > 2000000) {
						removeComponentFromModel(model);
						//	display error message
						displayMaxFileSizeErrorInAssetManager();
					}
				}
			});

			if (window.GM_BRANDING_TYPE_REPORT_EBOOK) {
				editor.on('component:drag:start', ({ target }) => {
					const styles = target.getStyle();
					if (!styles.transform) {
						return;
					}

					const { width, height } = frameStyles;
					const translateMatches = styles.transform.match(
						/.?translateX\((.+)vw\) translateY\((.+)vh\)/,
					);
					if (translateMatches?.length > 2) {
						const tX = parseFloat(translateMatches[1]);
						const tY = parseFloat(translateMatches[2]);
						const tXPx = (tX * width) / 100;
						const tYPx = (tY * height) / 100;
						const transform = `translateX(${tXPx}px) translateY(${tYPx}px)`;
						target.setStyle({
							...styles,
							transform,
						});
					}
				});

				editor.on('component:drag:end', ({ target }) => {
					const styles = target.getStyle();
					const { width, height } = frameStyles;
					const translateMatches = styles.transform.match(
						/.?translateX\((.+)px\) translateY\((.+)px\)/,
					);
					if (translateMatches?.length > 2) {
						const tX = parseFloat(translateMatches[1]);
						const tY = parseFloat(translateMatches[2]);
						const tXVW = (tX / width) * 100;
						const tYVH = (tY / height) * 100;
						const transform = `translateX(${tXVW}vw) translateY(${tYVH}vh)`;
						target.setStyle({
							...styles,
							transform,
						});
					}
				});

				// editor.on('component:deselected', component => {
				// 	convertFontSizeToRemEbookIntro(component);
				// });
				editor.on('component:selected', (component) => {
					doInComponentSelected(editor, component);
					const sizeConversionFactor = (1 / frameStyles.width) * 100;
					component.set({
						// draggable: '#wrapper',
						resizable: {
							minDim: 10,
							onStart: () => {
								// magic function to keep the from pos haywire
							},
							updateTarget: (el, rect) => {
								if (!el) {
									return;
								}
								setStyleGrapesJSComponent(`#${el.id}`, {
									...component.getStyle(),
									width: convertPxToRelative(rect.w, sizeConversionFactor, 'vw'),
									height: convertPxToRelative(rect.h, sizeConversionFactor, 'vw'),
								});
							},
						},
					});
				});
			} else {
				editor.on('component:selected', (component) => {
					doInComponentSelected(editor, component);
				});
				editor.on('component:deselected', handleComponentDeselected);
				editor.on('component:remove:before', (options) => {
					console.log('delete component', options);
				});
			}
		},
		[templateId],
	);

	const renderOpenDrawerIcon = () => {
		return (
			<div className={classes.openDrawerWrapperStyle}>
				<IconButton onClick={toggleDrawer} classes={{ root: classes.openDrawerIconStyle }}>
					<ChevronRightRounded />
				</IconButton>
			</div>
		);
	};

	const editorElement = useMemo(() => {
		const adviserInfo = pick(templateData, 'adviserData', 'contactForm', 'extraProfile');
		const editorConfig = getEditorConfig(templateId, templateType, adviserInfo);
		console.log('GetEditorConfig', editorConfig);
		return (
			<Editor
				ref={editorRef}
				onInit={onInit}
				drawerOpen={drawerOpen}
				commands={[
					createWidgetItemCommand(),
					createAccoladeItemCommand(),
					createVideoItemCommand(),
					createServiceItemCommand(),
					createChangeBackgroundCommand({ openChangeBackgroundDialogOpen }),
					createOpenEditorDialogCommand({ openConfigurationDialog }),
					createLayoutEditorCommand({ openLayoutDialog }),
				]}
				{...editorConfig}
			>
				<div />
			</Editor>
		);
	}, [onInit]);

	return (
		<>
			<Drawer
				variant="persistent"
				anchor="left"
				open={drawerOpen}
				PaperProps={{
					className: drawerOpen ? 'opened' : 'closed',
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
				onClose={toggleDrawer}
			>
				<EditorLeftPanel editor={editorRef} toggleDrawer={toggleDrawer} />
			</Drawer>
			{!drawerOpen && renderOpenDrawerIcon()}
			{editorElement}
			{!templateLoaded && (
				<div className={classes.progressContainer}>
					<CircularProgress />
				</div>
			)}
			{changeBackgroundDialogOptions && (
				<ChangeBackgroundDialog
					sectionId={sectionId}
					open={!!changeBackgroundDialogOptions}
					handleClose={closeChangeBackgroundDialog}
					options={changeBackgroundDialogOptions}
				/>
			)}
			{configurationEditorOpen && (
				<EditConfigurationDialog
					sectionId={sectionId}
					videoContainerId={videoContainerId}
					widgetContainerId={widgetContainerId}
					open={configurationEditorOpen}
					handleClose={toggleConfigureDialog}
				/>
			)}
		</>
	);
};

EditorScreen.propTypes = {};

export default memo(EditorScreen);
